import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { getTheme, useThemeMode, RADIUS, TAP } from '../theme';
import { auth, resolveUrl, QidUser, QidProvider, InviteRequiredError } from '../api';
import { isInviteRequiredBody, pendingMarkInvite } from './invite/inviteRequired';
import { Button, Field, Input, Segmented, IconButton } from './ui';
import { BrandLogo } from './BrandLogo';

export function LoginModal({ onClose, onLoggedIn }: { onClose: () => void; onLoggedIn: (u: QidUser) => void }) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  const [loginMode, setLoginMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [reg, setReg] = useState({ given_name: '', family_name: '', email: '', password: '', confirm: '', invite: '' });
  // IK1: felraden under Invitekod (fel kod); andra fel visas i errBox.
  const [inviteErr, setInviteErr] = useState('');
  // Inbjudningslänk till en mark ersätter koden (API:t kontrollerar länken).
  const hasMarkInvite = !!pendingMarkInvite();
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  // App Store (riktlinje 4.8) kräver Apple bredvid Google; visas när Qid har Apple påslaget.
  const [appleEnabled, setAppleEnabled] = useState(false);
  useEffect(() => { auth.appleEnabled().then(setAppleEnabled); }, []);

  const hLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) { setErr('Fyll i e-post och lösenord'); return; }
    setLoading(true); setErr('');
    try {
      const u = await auth.directLogin(email, password);
      onLoggedIn(u); onClose();
    } catch (ex: unknown) {
      if (ex instanceof InviteRequiredError) { onClose(); return; } // IK1: InviteCodeGate tar över
      setErr(ex instanceof Error ? ex.message : 'Inloggning misslyckades');
    }
    finally { setLoading(false); }
  };

  const hQid = async (provider: QidProvider = 'google') => {
    setLoading(true); setErr('');
    try {
      const u = await auth.loginWithQid(provider);
      if (u) { onLoggedIn(u); onClose(); } // webben lämnar sidan och kommer tillbaka via /auth/callback
    } catch (ex: unknown) {
      if (ex instanceof InviteRequiredError) { onClose(); return; } // IK1: InviteCodeGate tar över
      const msg = ex instanceof Error ? ex.message : '';
      if (!/avbr|cancel|access_denied/i.test(msg)) setErr(msg || `Inloggning med ${provider === 'apple' ? 'Apple' : 'Google'} misslyckades`);
      setLoading(false);
    }
  };

  const hRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (reg.password !== reg.confirm) { setErr('Lösenorden matchar inte'); return; }
    if (reg.password.length < 8) { setErr('Lösenordet måste vara minst 8 tecken'); return; }
    if (!reg.email) { setErr('Ange e-postadress'); return; }
    if (!hasMarkInvite && !reg.invite.trim()) { setInviteErr('Ange din invitekod'); return; }
    setLoading(true); setErr(''); setInviteErr('');
    try {
      const res = await fetch(resolveUrl('/auth/register'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({
        email: reg.email, password: reg.password, given_name: reg.given_name || undefined, family_name: reg.family_name || undefined,
        inviteCode: reg.invite.trim() || undefined, inviteToken: pendingMarkInvite() ?? undefined,
      }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (isInviteRequiredBody(data)) setInviteErr(data.error || 'Fel invitekod');
        else setErr(data.error || 'Registrering misslyckades');
        return;
      }
      auth.setTokens(data.token, data.token);
      const meRes = await fetch(resolveUrl('/auth/me'), { headers: { Authorization: `Bearer ${data.token}` } }).catch(() => null);
      const me = meRes?.ok ? await meRes.json() : data.user;
      auth.setUser(me);
      onLoggedIn(me); onClose();
    } catch (ex: unknown) { setErr(ex instanceof Error ? ex.message : 'Anslutningsfel'); }
    finally { setLoading(false); }
  };

  const errBox = err && (
    <div role="alert" style={{ background: T.dangerSoft, borderRadius: RADIUS.sm, padding: '10px 14px', color: T.danger, fontSize: 14, fontWeight: 600, marginBottom: 14 }}>{err}</div>
  );
  // Google och Apple har egna utseendekrav (vit knapp med kontur) – därför inte Button.
  const brandBtn: React.CSSProperties = { width: '100%', minHeight: TAP, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, background: '#fff', borderRadius: RADIUS.md, padding: '10px 14px', fontSize: 15, fontWeight: 600, cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.7 : 1 };

  return (
    <div style={{ position: 'fixed', inset: 0, background: T.scrim, zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, overflowY: 'auto' }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-label="Logga in på Qvenari" style={{ position: 'relative', background: T.surface, border: `1px solid ${T.border}`, borderRadius: RADIUS.xl, boxShadow: T.shadow.raised, width: '100%', maxWidth: 400, padding: '28px 24px 22px', margin: 'auto', color: T.text }}>
        <IconButton label="Stäng" onClick={onClose} style={{ position: 'absolute', top: 6, right: 6 }}><X size={20} strokeWidth={2} /></IconButton>
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <BrandLogo size={34} />
          <div style={{ color: T.textSub, fontSize: 14, marginTop: 8 }}>{loginMode === 'login' ? 'Logga in för att fortsätta' : 'Skapa ditt konto'}</div>
        </div>

        <Segmented label="Logga in eller registrera" value={loginMode} onChange={m => { setLoginMode(m); setErr(''); }}
          options={[{ value: 'login', label: 'Logga in' }, { value: 'register', label: 'Registrera' }]} style={{ marginBottom: 18 }} />

        <button type="button" onClick={() => hQid('google')} disabled={loading} style={{ ...brandBtn, color: '#1f1f1f', border: `1px solid ${T.borderHover}` }}>
          <GoogleLogo />Fortsätt med Google
        </button>
        {appleEnabled && (
          // Apples riktlinjer: "vit med kontur", Apple-loggan före texten, minst lika stor som Google-knappen.
          <button type="button" onClick={() => hQid('apple')} disabled={loading} aria-label="Fortsätt med Apple" style={{ ...brandBtn, marginTop: 8, gap: 8, color: '#000', border: '1px solid #000', fontFamily: '-apple-system, BlinkMacSystemFont, "Helvetica Neue", Helvetica, Arial, sans-serif' }}>
            <AppleLogo />Fortsätt med Apple
          </button>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '18px 0', color: T.textMuted, fontSize: 13 }}>
          <div style={{ flex: 1, height: 1, background: T.border }} />eller med e-post<div style={{ flex: 1, height: 1, background: T.border }} />
        </div>

        {loginMode === 'login' && (
          <form onSubmit={hLogin}>
            <Field label="E-postadress" style={{ marginBottom: 14 }}><Input type="email" autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} autoFocus placeholder="din@email.se" /></Field>
            <Field label="Lösenord" style={{ marginBottom: 16 }}><Input type="password" autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" /></Field>
            {errBox}
            <Button type="submit" block size="lg" loading={loading}>{loading ? 'Loggar in…' : 'Logga in'}</Button>
          </form>
        )}

        {loginMode === 'register' && (
          <form onSubmit={hRegister}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
              <Field label="Förnamn"><Input autoComplete="given-name" value={reg.given_name} onChange={e => setReg(r => ({ ...r, given_name: e.target.value }))} autoFocus placeholder="Anna" /></Field>
              <Field label="Efternamn"><Input autoComplete="family-name" value={reg.family_name} onChange={e => setReg(r => ({ ...r, family_name: e.target.value }))} placeholder="Svensson" /></Field>
            </div>
            <Field label="E-post *" style={{ marginBottom: 14 }}><Input type="email" autoComplete="email" value={reg.email} onChange={e => setReg(r => ({ ...r, email: e.target.value }))} required placeholder="din@email.se" /></Field>
            <Field label="Lösenord *" style={{ marginBottom: 14 }}><Input type="password" autoComplete="new-password" value={reg.password} onChange={e => setReg(r => ({ ...r, password: e.target.value }))} required placeholder="Minst 8 tecken" /></Field>
            <Field label="Bekräfta lösenord *" style={{ marginBottom: 14 }}><Input type="password" autoComplete="new-password" value={reg.confirm} onChange={e => setReg(r => ({ ...r, confirm: e.target.value }))} required placeholder="Upprepa lösenord" /></Field>
            <Field label={hasMarkInvite ? 'Invitekod' : 'Invitekod *'} hint={hasMarkInvite ? 'Behövs inte – du har en inbjudan till en jaktmark' : 'Qvenari är i stängd beta'} error={inviteErr || undefined} style={{ marginBottom: 16 }}>
              <Input value={reg.invite} onChange={e => { setReg(r => ({ ...r, invite: e.target.value })); setInviteErr(''); }} required={!hasMarkInvite}
                autoComplete="off" autoCapitalize="none" autoCorrect="off" spellCheck={false} />
            </Field>
            {errBox}
            <Button type="submit" block size="lg" loading={loading}>{loading ? 'Skapar konto…' : 'Skapa konto'}</Button>
          </form>
        )}

        <div style={{ marginTop: 14, textAlign: 'center', fontSize: 14 }}>
          <a href="/anslut" style={{ color: T.accentText, fontWeight: 600 }}>Gäst i en jakt? Anslut med kod utan konto</a>
        </div>
        <div style={{ marginTop: 16, textAlign: 'center', color: T.textMuted, fontSize: 13 }}>Ditt Qid-konto gäller på alla Q-tjänster</div>
        <div style={{ marginTop: 8, textAlign: 'center', color: T.textMuted, fontSize: 12, lineHeight: 1.5 }}>
          Genom att fortsätta godkänner du Qvenaris <a href="/villkor" style={{ color: T.accentText }}>användarvillkor</a> och bekräftar att du läst <a href="/integritet" style={{ color: T.accentText }}>integritetspolicyn</a>.
        </div>
      </div>
    </div>
  );
}

function AppleLogo() {
  return (
    <svg width="16" height="19" viewBox="0 0 24 24" aria-hidden="true" style={{ marginTop: -2 }}>
      <path fill="#000" d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
    </svg>
  );
}

function GoogleLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </svg>
  );
}
